'use client';

import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { Bell, SlidersHorizontal, X } from 'lucide-react';
import SortDropdown from '@/components/SortDropdown';
import LazyFilterPanel, { preloadFilterPanel } from '@/components/filters/LazyFilterPanel';
import QuickFilters from '@/components/filters/QuickFilters';
import { useResultsNavigation } from '@/components/filters/use-results-navigation';
import { activeCount, readFilters } from '@/lib/filter-state';
import ScrollRow from '@/components/ui/ScrollRow';
import { filterChips, withoutChip, withoutFilters, type FilterChip } from '@/lib/filter-chips';
import { numberFormat } from '@/lib/format';

// "Save search" and its email-code steps load when the visitor is about to
// use them - hover, focus or touch on the button - not with every list
// (speed, 2026-09-25).
const loadSaveSearch = () => import('@/components/SaveSearchModal');
const SaveSearchModal = dynamic(loadSaveSearch, { ssr: false });
let saveSearchLoading: Promise<unknown> | null = null;
const preloadSaveSearch = () => {
  saveSearchLoading ??= loadSaveSearch();
};

// The bar over a list of results (listing browse plan, 2026-09-23). It sticks
// to the top of the screen while the list scrolls - under the site header when
// the header shows, in its place when it hides - and holds exactly three
// buttons - Filters, Sort, Save search - and, only when something narrows the
// list, one chip per filter with its own × and a "Clear all".

const button =
  'h-11 flex items-center justify-center gap-2 px-3 rounded-xl border border-neutral-800 bg-neutral-900 text-sm font-semibold text-neutral-100 cursor-pointer';

export default function ResultsBar({
  lang,
  total,
  fixedParams,
  agencyId = null,
  bleed = '-mx-2.5 px-2.5 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8',
}: {
  lang: string;
  total: number;
  /** Filters the page applies by itself (a city page's city), for a saved search and the panel's count. */
  fixedParams?: Record<string, string>;
  agencyId?: string | null;
  /** Negative margins matching the page's side padding, so the bar's background spans it. */
  bleed?: string;
}) {
  const isFr = lang === 'fr';
  const searchParams = useSearchParams();
  const navigate = useResultsNavigation();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  // Mounted from the first open on, so it can close with its own animation.
  const [saveWanted, setSaveWanted] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  // Its height, for the page's scroll padding: a card that takes keyboard
  // focus is scrolled into view below the bar, not under it.
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const root = document.documentElement;
    const observer = new ResizeObserver(() => root.style.setProperty('--results-bar-h', `${el.offsetHeight}px`));
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--results-bar-h');
    };
  }, []);

  const current = new URLSearchParams(searchParams?.toString() || '');
  const chips = filterChips(current, lang);
  const filtersOn = activeCount(readFilters(current));
  const go = (next: URLSearchParams) => navigate(next, 'replace');

  const count = numberFormat(isFr ? 'fr-CA' : 'en-CA').format(total);
  const countText = isFr
    ? `${count} ${total === 1 ? 'annonce' : 'annonces'}`
    : `${count} ${total === 1 ? 'listing' : 'listings'}`;

  const chipLabel = (chip: FilterChip) => (isFr ? `Retirer le filtre ${chip.label}` : `Remove filter ${chip.label}`);

  return (
    <>
      <div ref={barRef} className={`sticky under-header transition-[top] duration-200 motion-reduce:transition-none z-30 ${bleed} py-2 bg-black/95 backdrop-blur-md border-b border-neutral-800/80`}>
        <div className="flex items-center gap-3">
          <p className="hidden sm:block flex-1 min-w-0 truncate text-base font-bold text-white" aria-live="polite">
            {countText}
          </p>
          {/* A wide screen gets the main filters as buttons of their own. */}
          <div className="hidden lg:block">
            <QuickFilters lang={lang} onOpenAll={() => setFiltersOpen(true)} rent={fixedParams?.deal === 'rent'} />
          </div>
          {/* On a phone Filters is an icon (with its count), sorting shows
              what the list is sorted by, saving is the small one at the end. */}
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              type="button"
              data-hint="filters"
              onClick={() => setFiltersOpen(true)}
              onMouseEnter={preloadFilterPanel}
              onFocus={preloadFilterPanel}
              onTouchStart={preloadFilterPanel}
              className={`${button} lg:hidden`}
              aria-haspopup="dialog"
              aria-label={isFr ? 'Filtres' : 'Filters'}
            >
              <SlidersHorizontal aria-hidden="true" className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">{isFr ? 'Filtres' : 'Filters'}</span>
              {filtersOn > 0 && (
                <span className="min-w-5 h-5 px-1 rounded-full bg-gold text-black text-xs font-bold flex items-center justify-center">
                  {filtersOn}
                </span>
              )}
            </button>
            <SortDropdown lang={lang} variant="bar" />
            <button
              type="button"
              data-hint="save-search"
              onClick={() => {
                setSaveWanted(true);
                setSaveOpen(true);
              }}
              onMouseEnter={preloadSaveSearch}
              onFocus={preloadSaveSearch}
              onTouchStart={preloadSaveSearch}
              className={button}
              aria-haspopup="dialog"
            >
              <Bell className="w-4 h-4 shrink-0" />
              <span className="sm:hidden">{isFr ? 'Alerte' : 'Save'}</span>
              <span className="hidden sm:inline">{isFr ? 'Créer une alerte' : 'Save search'}</span>
            </button>
          </div>
        </div>

        {chips.length > 0 && (
          <ScrollRow wrapperClassName="relative mt-2" className="flex gap-2 overflow-x-auto scrollbar-none sm:flex-wrap">
            <button
              type="button"
              onClick={() => go(withoutFilters(current))}
              className="shrink-0 h-9 px-3 rounded-full text-sm font-semibold text-champagne underline underline-offset-2 cursor-pointer"
            >
              {isFr ? 'Tout effacer' : 'Clear all'}
            </button>
            {chips.map((chip) => (
              <button
                key={`${chip.key}:${chip.value ?? ''}`}
                type="button"
                onClick={() => go(withoutChip(current, chip))}
                aria-label={chipLabel(chip)}
                className="shrink-0 h-9 pl-3 pr-2 rounded-full border border-neutral-700 bg-neutral-900 text-sm text-neutral-100 flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
              >
                <span>{chip.label}</span>
                <X aria-hidden="true" className="w-4 h-4 text-neutral-400" />
              </button>
            ))}
          </ScrollRow>
        )}
      </div>

      {/* On a phone the bar is three buttons wide; the count goes under it. */}
      <p className="sm:hidden mt-3 text-base font-bold text-white">{countText}</p>

      <LazyFilterPanel isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} fixedParams={fixedParams} agencyId={agencyId} />
      {saveWanted && <SaveSearchModal isOpen={saveOpen} onClose={() => setSaveOpen(false)} lang={lang} extraParams={fixedParams} />}
    </>
  );
}
